/**
 * Adapter over the active editor's imperative ref (ActiveEditorContext). Prefers the editor
 * bridge API (getMeta/getBlocks/getSelection/applyEditPlan/getPendingReview/acceptAll/…,
 * CONTRACTS §6) and falls back to the older ref methods (getCleanHTMLForAiEdit,
 * showPatchDiffEdits, showDiffEdits, acceptAllChanges, rejectAllChanges) so Inky keeps
 * working against an editor build that predates the bridge.
 */

import { blocksAreEmpty, htmlHasStructure } from "./editHtml";

/**
 * Call an optional method on the editor handle without letting it throw into the agent.
 * @param {object|null} handle - Editor imperative handle (ref.current).
 * @param {string} method - Method name.
 * @param {...*} args - Arguments.
 * @returns {*} Method result, or undefined when missing or failing.
 */
const safeCall = (handle, method, ...args) => {
  if (!handle || typeof handle[method] !== "function") return undefined;
  try {
    return handle[method](...args);
  } catch (error) {
    console.warn(`[inky] editor.${method} failed`, error);
    return undefined;
  }
};

/**
 * The live editor handle behind a ref, or null when no editor is open.
 * @param {(() => object|null)|undefined} getEditorRef - ActiveEditorContext getter.
 * @returns {object|null} Editor handle.
 */
export const getEditorHandle = (getEditorRef) => {
  const ref = typeof getEditorRef === "function" ? getEditorRef() : null;
  const handle = ref?.current || null;
  if (!handle) return null;
  if (safeCall(handle, "isDestroyed") === true) return null;
  return handle;
};

/**
 * Whether the handle implements the block-based bridge (getBlocks + applyEditPlan).
 * @param {object|null} handle - Editor handle.
 * @returns {boolean} True for the bridge API.
 */
export const supportsEditPlans = (handle) =>
  Boolean(handle && typeof handle.getBlocks === "function" && typeof handle.applyEditPlan === "function");

/**
 * Strip tags/entities from HTML to plain text.
 * @param {string} html - HTML.
 * @returns {string} Text.
 */
export const htmlToText = (html) =>
  String(html || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|h[1-6]|li|tr)>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/​/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

/**
 * Synthesize an outline from HTML for editors without getBlocks (ids are positional and
 * prefixed `p`, so they are never sent as bridge block ids).
 * @param {string} html - Document HTML.
 * @returns {Array<{id: string, type: string, level?: number, text: string, html: string, locked: boolean, synthetic: boolean}>} Pseudo blocks.
 */
export const synthesizeBlocks = (html) => {
  if (!html || typeof DOMParser === "undefined") return [];
  try {
    const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
    return Array.from(doc.body.children).map((el, index) => {
      const tag = el.tagName.toLowerCase();
      const heading = /^h([1-6])$/.exec(tag);
      return {
        id: `p${index + 1}`,
        type: heading ? "heading" : tag === "ul" || tag === "ol" ? "list" : tag === "table" ? "table" : "paragraph",
        ...(heading ? { level: Number(heading[1]) } : {}),
        text: (el.textContent || "").trim(),
        html: el.outerHTML,
        locked: el.getAttribute("data-tpl-locked") === "true",
        synthetic: true,
      };
    });
  } catch {
    return [];
  }
};

/**
 * The current selection, from the bridge or the raw TipTap editor.
 * @param {object} handle - Editor handle.
 * @returns {{from: number, to: number, text: string, html?: string, blockIds: string[]}|null} Selection.
 */
const readSelection = (handle) => {
  const bridged = safeCall(handle, "getSelection");
  if (bridged !== undefined) return bridged && bridged.text ? bridged : null;
  const editor = handle.editor;
  const selection = editor?.state?.selection;
  if (!selection || selection.empty) return null;
  try {
    const text = editor.state.doc.textBetween(selection.from, selection.to, "\n").trim();
    return text ? { from: selection.from, to: selection.to, text, blockIds: [] } : null;
  } catch {
    return null;
  }
};

/**
 * Clean HTML for AI editing (without review markup) via whichever method exists.
 * @param {object} handle - Editor handle.
 * @returns {string} HTML.
 */
export const readEditorHtml = (handle) => {
  const clean = safeCall(handle, "getCleanHTMLForAiEdit");
  if (typeof clean === "string") return clean;
  const html = safeCall(handle, "getHTML");
  return typeof html === "string" ? html : "";
};

/**
 * Whether the open document holds nothing at all (see editHtml.blocksAreEmpty).
 * @param {{bridged: boolean, blocks: Array<object>, html: string, text: string}} snapshot - Snapshot parts.
 * @returns {boolean} True when structurally empty.
 */
const isSurfaceEmpty = ({ bridged, blocks, html, text }) => {
  if (String(text || "").trim()) return false;
  if (bridged || blocks.length) return blocksAreEmpty(blocks);
  // Legacy editor and no DOMParser to split the html: fall back to the markup itself.
  return !htmlHasStructure(html);
};

/**
 * Snapshot the open editor for the prompt and the doc tools.
 * @param {(() => object|null)|undefined} getEditorRef - ActiveEditorContext getter.
 * @param {{fallbackMeta?: object}} [options] - Route-derived meta for editors without getMeta.
 * @returns {object|null} Surface snapshot, or null when no editor is open.
 */
export const snapshotEditor = (getEditorRef, { fallbackMeta = null } = {}) => {
  const handle = getEditorHandle(getEditorRef);
  if (!handle) return null;
  const bridged = supportsEditPlans(handle);
  const meta = { ...(fallbackMeta || {}), ...(safeCall(handle, "getMeta") || {}) };
  const html = bridged ? "" : readEditorHtml(handle);
  const blocks = bridged ? safeCall(handle, "getBlocks") || [] : synthesizeBlocks(html);
  const text = bridged ? blocks.map((block) => block.text || "").join("\n") : htmlToText(html);
  const pending = safeCall(handle, "getPendingReview");
  const pendingReview =
    pending !== undefined
      ? pending
      : handle.isDiffMode
        ? { reviewId: null, changes: [{ id: "legacy", kind: "edit", summary: "Pending AI changes" }] }
        : null;
  return {
    handle,
    bridged,
    meta,
    blocks,
    html,
    selection: readSelection(handle),
    pendingReview: pendingReview && pendingReview.changes?.length ? pendingReview : null,
    // Structural, not textual: a document holding only an image, a diagram, an empty table or a
    // trust section has no text but is NOT empty, and must never be drafted over wholesale.
    isEmpty: isSurfaceEmpty({ bridged, blocks, html, text }),
    wordCount: text.trim() ? text.trim().split(/\s+/).length : 0,
  };
};

/**
 * Accept every pending suggestion.
 * @param {object|null} handle - Editor handle.
 * @returns {boolean} True when the editor accepted the call.
 */
export const acceptAllSuggestions = (handle) => {
  if (!handle) return false;
  if (typeof handle.acceptAll === "function") return safeCall(handle, "acceptAll") !== false;
  if (typeof handle.acceptAllChanges === "function") return safeCall(handle, "acceptAllChanges") !== false;
  return false;
};

/**
 * Reject every pending suggestion (undo the AI edit).
 * @param {object|null} handle - Editor handle.
 * @returns {boolean} True when the editor accepted the call.
 */
export const rejectAllSuggestions = (handle) => {
  if (!handle) return false;
  if (typeof handle.rejectAll === "function") return safeCall(handle, "rejectAll") !== false;
  if (typeof handle.rejectAllChanges === "function") return safeCall(handle, "rejectAllChanges") !== false;
  return false;
};

/**
 * Run a callback after the browser has painted twice (TipTap runs its focus work in one
 * animation frame; the second frame comes after it).
 * @param {() => void} callback - Callback.
 * @returns {void}
 */
const afterNextFrames = (callback) => {
  if (typeof window === "undefined" || typeof window.requestAnimationFrame !== "function") {
    setTimeout(callback, 32);
    return;
  }
  window.requestAnimationFrame(() => window.requestAnimationFrame(callback));
};

/**
 * Focus the editor and bring a block into view so the user can review changes.
 *
 * Focus comes FIRST and asks for no scroll: TipTap's focus command otherwise scrolls the old
 * selection into view one animation frame later (the cursor from before the review, or the end
 * of the document after a refined review), overriding the scroll to the change. The block scroll
 * runs after that frame, so it wins even on an editor whose focus() ignores the option.
 * @param {object|null} handle - Editor handle.
 * @param {string} [blockId] - Block to reveal.
 * @returns {void}
 */
export const revealInEditor = (handle, blockId) => {
  if (!handle) return;
  safeCall(handle, "focus", null, { scrollIntoView: false });
  if (!blockId) return;
  afterNextFrames(() => {
    if (safeCall(handle, "isDestroyed") === true) return;
    safeCall(handle, "scrollToBlock", blockId);
  });
};

/**
 * The review the editor is showing now.
 * @param {object|null} handle - Editor handle.
 * @returns {{reviewId: string|null, changes: Array<object>}|null|undefined} The pending review, null when
 *   none is open, or undefined when the editor cannot tell (no getPendingReview).
 */
export const getPendingReview = (handle) => {
  if (!handle || typeof handle.getPendingReview !== "function") return undefined;
  const pending = safeCall(handle, "getPendingReview");
  return pending && pending.changes?.length ? pending : null;
};

/**
 * Subscribe to an editor event ('review-started' | 'review-resolved' | ...) when supported.
 * @param {object|null} handle - Editor handle.
 * @param {string} event - Event name.
 * @param {(payload: object) => void} callback - Listener.
 * @returns {(() => void)|null} Unsubscribe function, or null when unsupported.
 */
export const onEditorEvent = (handle, event, callback) => {
  const unsubscribe = safeCall(handle, "on", event, callback);
  return typeof unsubscribe === "function" ? unsubscribe : null;
};

/**
 * Subscribe to the editor's review-resolved event when supported.
 * @param {object|null} handle - Editor handle.
 * @param {(payload: object) => void} callback - Listener.
 * @returns {(() => void)|null} Unsubscribe function, or null when unsupported.
 */
export const onReviewResolved = (handle, callback) => onEditorEvent(handle, "review-resolved", callback);
